import React, { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useMutation } from '@apollo/client/react';
import cn from 'clsx';
import i18next from 'i18next';
import styles from './style.module.scss';
import { BasicDropDownController } from '@components-clean/core/basic-dropdown';
import CoreCheckbox from '@components-clean/core/core-checkbox';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import EditCard from '@components-clean/core/edit-card';
import { UnfilterableIcon } from '@components-clean/core/icons/unfilterable-icon/unfilterable-icon';
import { InfoField } from '@components-clean/core/info-field';
import Loading from '@components-clean/core/loading/loading';
import { TextBox, TextBoxController } from '@components-clean/core/text-box';
import { useUnsavedChangesContext } from '@contexts-clean/unsaved-changes-context';
import { DataViewDatatables } from '@pages-clean/admin/study/admin-data-views/data-views/datatables/data-view-datatables';
import { EditableDataViewConfigForm } from '@pages-clean/admin/study/admin-data-views/data-views/editable-lists/editable-data-view-config-form';
import { DataViewFilters } from '@pages-clean/admin/study/admin-data-views/data-views/filters/data-view-filters';
import { DataViewPanels } from '@pages-clean/admin/study/admin-data-views/data-views/panels/data-view-panels';
import {
    DataViewConfig,
    DataViewConfigForm,
    PanelData,
} from '@pages-clean/admin/study/admin-data-views/data-views/types';
import { DataFilterResultType } from '@pages-clean/admin/study/admin-data-views/filters/types';
import { isEmptyObjectId, isValidObjectId } from '@utils-clean/validation';
import { DataViewConfigValidation } from 'types/graphql-global-types';
import { IUser } from '@contexts/user-context';
import { useBoolean, useTranslation } from '@hooks/index';
import Constants from '@utils/constants';
import NotificationService from '@utils/notification';
import { DELETE_DATA_VIEW, UPDATE_DATA_VIEW } from '@queries/central-monitoring';
import { dataReviewTables, updateDataViewConfiguration } from '@queries/generated/central-monitoring.generated';
import { useGetObsActionsLogsCount } from '@apis/bff-api/central-monitoring';

type DataViewDetailProps = {
    dataViewConfig: DataViewConfig;
    panelsData: PanelData[];
    dataTablesData?: dataReviewTables;
    filtersData: DataFilterResultType[];
    setIsEditMode: Dispatch<SetStateAction<boolean>>;
    isEditMode: boolean;
    onDelete: (id: string) => void;
    onUpdate: (dataView: DataViewConfig) => void;
    user: IUser;
    existingViews: DataViewConfig[];
    studyId: string;
};

const COMPONENT_NAME = 'data-view-details';

const DataViewDetails = ({
    dataViewConfig,
    panelsData,
    dataTablesData,
    filtersData,
    setIsEditMode,
    isEditMode,
    onDelete,
    onUpdate,
    user,
    existingViews,
    studyId,
}: DataViewDetailProps) => {
    const {
        control,
        handleSubmit,
        reset,
        // Need to subscribe to the errors state, so that the nameFieldState.invalid gets updated correctly
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        formState: { isDirty, errors },
        getFieldState,
        watch,
        setValue,
    } = useFormContext<DataViewConfigForm>();
    const nameFieldState = getFieldState('name');
    const watchUnfilterablePanelIds = watch('unfilterablePanelIds');
    const watchMainPanel = watch('mainPanel');

    const showDelete = useBoolean(false);
    const showInUse = useBoolean(false);
    const showMainPanelWarning = useBoolean(false);
    const { t } = useTranslation();
    const [deleteDataView] = useMutation(DELETE_DATA_VIEW);
    const [updateDataView] = useMutation<updateDataViewConfiguration>(UPDATE_DATA_VIEW);

    const canEdit = user.canWriteSystemAdminLevel?.() ?? false;
    const isSysAdmin = user.systemAdminLevel === Constants.SystemAdminLevel.SysAdmin;
    const [newMainPanel, setNewMainPanel] = useState<string | null>(dataViewConfig.mainPanelId);
    const [dvConfigMainPanel, setDVConfigMainPanel] = useState(dataViewConfig.mainPanelId);
    const { setComponentHasChanges, componentHasChanges, confirmDiscardChanges } = useUnsavedChangesContext();

    useEffect(() => {
        setComponentHasChanges(COMPONENT_NAME, isDirty && isEditMode);
        return () => {
            setComponentHasChanges(COMPONENT_NAME, false);
        };
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [isDirty, isEditMode]);

    const { data: obsActionsLogsCount, mutate, error } = useGetObsActionsLogsCount(studyId, dataViewConfig.mainPanelId);

    useEffect(() => {
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        /* eslint-disable @eslint-react/set-state-in-effect */
        setDVConfigMainPanel(dataViewConfig.mainPanelId);
        setNewMainPanel(dataViewConfig.mainPanelId);
        /* eslint-enable @eslint-react/set-state-in-effect */
    }, [dataViewConfig]);

    const mainPanelPlotTitle = panelsData?.find((panel) => panel?.id === dataViewConfig.mainPanelId)?.title;

    const deleteDataViewRecord = (data: DataViewConfig) => {
        deleteDataView({
            variables: {
                reason: '',
                configId: data.id,
            },
        })
            .then((result) => {
                if (result?.data?.deleteDataViewConfiguration !== 'SUCCESS') {
                    NotificationService.showNotification({ text: t('errorMessage'), type: 'error' });
                } else {
                    NotificationService.showNotification({
                        text: t('common:actionsSuccessfulToast'),
                        type: 'success',
                    });
                }
                onDelete(data.id);
                showDelete.setFalse();
            })
            .catch((err) => {
                NotificationService.showNotification({ text: t('errorMessage'), type: 'error' });
                window.console.error(err);
            });
    };

    const studyDataTables = useMemo(() => {
        return (dataTablesData?.dataReviewTablesForStudy || []).flatMap((item) => {
            return item == null ? [] : [{ ...item }];
        });
    }, [dataTablesData?.dataReviewTablesForStudy]);

    const submit = (data: DataViewConfigForm) => {
        const dataToSave = {
            configId: data.configId,
            dataFilterIds: data.dataFilterIds,
            dataSource: data.dataSource,
            dataTableIds: data.dataTableIds,
            displayIndex: data.displayIndex,
            id: data.id,
            isActive: data.isActive,
            mainPanelId: data.mainPanel!.id, // Validation should ensure main panel is populated
            name: data.name,
            panelIds: data.panelIds,
            recordId: data.recordId,
            unfilterablePanelIds: data.unfilterablePanelIds,
            studyId,
        };

        updateDataView({
            variables: { config: dataToSave, reason: '' },
        })
            .then((result) => {
                if (result?.data?.updateDataViewConfiguration?.validation === DataViewConfigValidation.SUCCESS) {
                    setIsEditMode(false);
                    NotificationService.showNotification({ text: t('actionsSuccessfulToast'), type: 'success' });
                    onUpdate(result.data.updateDataViewConfiguration.result as DataViewConfig);
                    showMainPanelWarning.setFalse();
                }
            })
            .catch((err) => {
                NotificationService.showNotification({ text: t('errorMessage'), type: 'error' });
                window.console.error(err);
            });
    };

    const cancelEdit = () => {
        setIsEditMode(false);
        setNewMainPanel(dataViewConfig.mainPanelId);
        reset();
    };

    const readonlyChildComponents = (
        <>
            <div className={cn('k-d-flex k-gap-4.5 k-h-full', styles.readOnlyCard, styles.readOnlyCard45)}>
                <div className={styles.readOnlyPanelCard}>
                    <DataViewPanels
                        studyPanels={panelsData}
                        dataViewPanelIds={dataViewConfig.panelIds}
                        unfilterablePanelIds={dataViewConfig.unfilterablePanelIds}
                    />
                </div>
                <div className={styles.readOnlyDataTablesCard}>
                    <DataViewDatatables
                        studyDataViewDataTables={studyDataTables}
                        dataViewDataTableIds={dataViewConfig.dataTableIds}
                    />
                </div>
            </div>
            <div className={cn('k-h-full', styles.readOnlyCard, styles.readOnlyCard40)}>
                <DataViewFilters dataViewFilters={filtersData} dataViewFilterIds={dataViewConfig.dataFilterIds} />
            </div>
        </>
    );

    const editableChildComponents = (
        <EditableDataViewConfigForm
            canEdit={canEdit}
            activeDataView={dataViewConfig}
            studyPanels={panelsData}
            studyDataTables={studyDataTables}
            dataViewFilters={filtersData}
        />
    );

    const onSubmit = async (data: DataViewConfigForm) => {
        if (newMainPanel !== dvConfigMainPanel) {
            const newObsActionsLogsCount = await mutate();
            if (error) {
                NotificationService.showNotification({ text: t('errorMessage'), type: 'error' });
                window.console.error(error);
            } else if (newObsActionsLogsCount?.observationsCount !== 0) showMainPanelWarning.setTrue();
            else submit(data);
        } else {
            submit(data);
        }
    };

    const warningText = (
        <span className="k-text-left">
            {t('admin:mainPanelUpdateWarning')} <span className={styles.boldText}> {mainPanelPlotTitle} </span>
            {t('admin:mainPanelUpdateWarning1')}
        </span>
    );

    const mainPanelWarningMessage = (
        <div className={cn('k-w-full k-d-flex k-flex-column k-gap-1 k-align-items-flex-start', styles.fontSize)}>
            {warningText}
            <span className={styles.textHighLightWithPaddingTop}>
                {obsActionsLogsCount?.observationsCount} {t('admin:openObservations')}
            </span>
            <span className={styles.textHighLight}>
                {obsActionsLogsCount?.actionsCount} {t('admin:openActions')}
            </span>
            <div className={styles.paddingBottom}>
                <span className={styles.textHighLight}>
                    {obsActionsLogsCount?.reviewLogsCount} {t('admin:reviewLogs')}
                </span>{' '}
                {t('admin:mainPanelUpdateWarning4')}
            </div>

            <span>{t('admin:mainPanelUpdateWarning2')}</span>
        </div>
    );

    const mainPanelNonFilterable = () => watchMainPanel && watchUnfilterablePanelIds?.includes(watchMainPanel.id);

    return (
        <div className="k-h-full">
            {showMainPanelWarning.value && (
                <DraggableDialog
                    width={608}
                    height={331}
                    primaryBtnLabel={t('admin:continue')}
                    title={t('admin:mainPanelUpdateWarningDialog')}
                    dataTestId="data-view-main-panel-update-warning"
                    onClose={() => {
                        showMainPanelWarning.setFalse();
                    }}
                    onSubmit={() => handleSubmit(submit)()}
                >
                    <div className="k-h-full k-d-flex k-flex-column k-gap-7">
                        <InfoField type="Warning" message={mainPanelWarningMessage} />
                        <span className="k-align-self-center k-py-5">{t('admin:mainPanelUpdateWarning3')}</span>
                    </div>
                </DraggableDialog>
            )}
            {showDelete.value && (
                <DraggableDialog
                    title={t('admin:dataView.deleteDataView', { name: dataViewConfig.name })}
                    dataTestId="data-view-config-delete-data-warning"
                    onClose={() => showDelete.setFalse()}
                    onSubmit={() => deleteDataViewRecord(dataViewConfig)}
                >
                    {t('admin:dataView.deleteDataViewDialog')}
                </DraggableDialog>
            )}
            {showInUse.value && (
                <DraggableDialog
                    dataTestId="data-view-config-inuse-data-warning"
                    primaryBtnLabel={t('ok')}
                    onSubmit={() => {
                        showInUse.setFalse();
                    }}
                    onClose={() => showInUse.setFalse()}
                    title={t('admin:dataView.inUseDataView', { name: dataViewConfig.name })}
                >
                    {t('admin:dataView.inUseDataViewDialog')}
                </DraggableDialog>
            )}
            <EditCard
                className="k-h-full"
                bodyClassName="k-d-flex k-d-flex-col"
                title={
                    dataViewConfig.recordId
                        ? `${t('admin:dataViewDetails')}: ${dataViewConfig.recordId}`
                        : t('admin:dataViewDetails')
                }
                onEdit={() => setIsEditMode(true)}
                onCancel={async () => {
                    if (componentHasChanges(COMPONENT_NAME)) {
                        const confirmed = await confirmDiscardChanges('data-view-config-cancel-confirmation');
                        if (!confirmed) {
                            return;
                        }
                        setComponentHasChanges(COMPONENT_NAME, false);
                    }
                    cancelEdit();
                }}
                onSave={() => handleSubmit((data) => onSubmit(data))()}
                isEditMode={isEditMode}
                data-testid="edit-data-view-card"
                disabled={!(canEdit || isSysAdmin)}
                saveDisabled={isEditMode && (!isDirty || nameFieldState.invalid)}
                disabledDelete={!canEdit}
                onDelete={() => {
                    dataViewConfig.inUse ? showInUse.setTrue() : showDelete.setTrue();
                }}
                bodyStyles={{ overflowY: 'auto' }}
            >
                {!dataTablesData?.dataReviewTablesForStudy ? ( // panelsLoading || filtersLoading || dataTablesLoading
                    <Loading />
                ) : (
                    <>
                        <div className="k-d-flex k-gap-5">
                            <div>
                                {t('admin:dataViewActive')}
                                <div className={styles.checkboxWrapper}>
                                    <Controller
                                        control={control}
                                        name="isActive"
                                        render={({ field: { onChange, value } }) => {
                                            return (
                                                <CoreCheckbox
                                                    data-testid="text-toggle"
                                                    checked={value}
                                                    onChange={onChange}
                                                    disabled={!(isEditMode && canEdit)}
                                                />
                                            );
                                        }}
                                    />
                                </div>
                            </div>
                            <div className={cn('k-d-flex k-gap-x-5 k-flex-wrap k-flex-auto', styles.inputWrapper)}>
                                <div>
                                    <TextBoxController
                                        control={control}
                                        name="name"
                                        data-testid="data-view-name"
                                        label={t('admin:dataViewName')}
                                        readOnly={!(isEditMode && canEdit)}
                                        required
                                        style={{ width: '375px' }}
                                        maxLength={30}
                                        validateRules={{
                                            uniqueValue: (value) => {
                                                if (
                                                    value &&
                                                    existingViews.find(
                                                        (x) =>
                                                            x.id !== dataViewConfig.id &&
                                                            x.name.toLocaleLowerCase().trim() ===
                                                                (value as string).toLocaleLowerCase().trim(),
                                                    )
                                                )
                                                    return i18next.t('admin:dataView.validation.uniqueName');
                                            },
                                        }}
                                    />
                                </div>
                                <div style={{ minWidth: '430px', flex: '1 1 auto' }}>
                                    <BasicDropDownController
                                        control={control}
                                        data-testid="data-view-main-panel"
                                        name="mainPanel"
                                        data={panelsData.filter(
                                            (p) =>
                                                p.active &&
                                                !existingViews.find(
                                                    (v) => v.mainPanelId === p.id && v.id !== dataViewConfig.id,
                                                ),
                                        )}
                                        textField="title"
                                        dataItemKey="id"
                                        label={t('admin:dataViewMainPanel')}
                                        readOnly={!(isEditMode && canEdit)}
                                        activeField="active"
                                        required
                                        handleInputChange={(event) => {
                                            let newUnfilterablePanelids = watchUnfilterablePanelIds;
                                            if (newMainPanel && watchUnfilterablePanelIds?.includes(newMainPanel))
                                                newUnfilterablePanelids = watchUnfilterablePanelIds.filter(
                                                    (id) => id !== newMainPanel,
                                                );

                                            if (event.value) {
                                                const selectedValue = event.value as PanelData;
                                                setNewMainPanel(selectedValue.id);

                                                if (selectedValue.isModel) {
                                                    if (newUnfilterablePanelids == null)
                                                        newUnfilterablePanelids = [selectedValue.id];
                                                    else if (!newUnfilterablePanelids.includes(selectedValue.id))
                                                        newUnfilterablePanelids.push(selectedValue.id);
                                                }
                                            } else {
                                                setNewMainPanel(null);
                                            }

                                            setValue('unfilterablePanelIds', newUnfilterablePanelids);
                                        }}
                                        suffix={
                                            mainPanelNonFilterable() ? (
                                                <UnfilterableIcon
                                                    dataTestId={`${newMainPanel}-unfilterable`}
                                                    className="k-opacity-80"
                                                    tooltip={t('cm:componentsClean.panels.screenFilterableWarning')}
                                                    isSuffix
                                                    defaultOpacity={75}
                                                />
                                            ) : undefined
                                        }
                                        filterable
                                        allowClear
                                    />
                                </div>
                                <div>
                                    <TextBox
                                        name="dataSource"
                                        data-testid="data-view-datasource"
                                        label={t('admin:dataViewDataSource')}
                                        value={`${t(`admin:${dataViewConfig.dataSource}`)}`}
                                        readOnly
                                        style={{ width: '320px' }}
                                    />
                                </div>
                                <div>
                                    <TextBoxController
                                        control={control}
                                        name="configId"
                                        data-testid="data-view-config-id"
                                        label={t('admin:configId')}
                                        value={dataViewConfig.configId}
                                        readOnly={!(isEditMode && isSysAdmin)}
                                        copyBtn
                                        required
                                        style={{ width: '180px' }}
                                        validateRules={{
                                            isObjectId: (value) => {
                                                if (
                                                    !isValidObjectId(value as string) ||
                                                    isEmptyObjectId(value as string)
                                                )
                                                    return t('admin:validConfigIdError');
                                            },
                                            uniqueValue: (value) => {
                                                if (
                                                    value &&
                                                    existingViews.find(
                                                        (x) =>
                                                            x.id !== dataViewConfig.id &&
                                                            x.configId.toLocaleLowerCase().trim() ===
                                                                (value as string).toLocaleLowerCase().trim(),
                                                    )
                                                )
                                                    return i18next.t('admin:uniqueConfigIdError');
                                            },
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                        <div
                            className="k-d-flex k-gap-4.5 k-flex-auto k-min-h-0"
                            style={{ paddingTop: 15, height: 'calc(100% - 78px)', flexWrap: 'wrap' }}
                        >
                            {isEditMode ? editableChildComponents : readonlyChildComponents}
                        </div>
                    </>
                )}
            </EditCard>
        </div>
    );
};
export default DataViewDetails;
